import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { can, errorText, MenuCategory, MenuItem, PosArea, PosFloor, PosMenu, posApi, PosRole, PosStaff, PosTable, rupees } from './api';
import { usePos } from './PosLayout';

type Tab = 'menu' | 'tables' | 'staff' | 'bill';

// Setup for managers: menu, tables, bill details; staff and PINs for the owner.
const SetupPage: React.FC = () => {
  const { me } = usePos();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'menu';
  const tabs: [Tab, string][] = [['menu', 'Menu'], ['tables', 'Tables'], ...(can(me.role, 'Owner') ? [['staff', 'Staff & PINs'] as [Tab, string]] : []), ['bill', 'Bill & GST']];
  const select = (t: Tab) => { const p = new URLSearchParams(params); p.set('tab', t); setParams(p, { replace: true }); };
  return (
    <>
      <h2 className="pos-h">Setup</h2>
      <div className="pos-tabs">{tabs.map(([k, label]) => <button key={k} className={`pos-tab ${tab === k ? 'on' : ''}`} onClick={() => select(k)}>{label}</button>)}</div>
      {tab === 'menu' && <MenuSetup />}
      {tab === 'tables' && <TablesSetup />}
      {tab === 'staff' && can(me.role, 'Owner') && <StaffSetup />}
      {tab === 'bill' && <BillSetup />}
    </>
  );
};

// ---------- menu ----------

type ItemDraft = Omit<MenuItem, 'id' | 'station' | 'variants'> & { id: number | null; variants: { id?: number; name: string; price: string }[] };

const blankItem = (categoryId: number): ItemDraft => ({
  id: null, categoryId, name: '', code: '', description: '', foodType: 'Veg', taxName: 'GST', taxRate: 5, isAvailable: true, sortOrder: 0,
  variants: [{ name: 'Regular', price: '' }]
});

function MenuSetup() {
  const [menu, setMenu] = useState<PosMenu | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cat, setCat] = useState<Partial<MenuCategory> | null>(null);
  const [item, setItem] = useState<ItemDraft | null>(null);
  const load = useCallback(() => posApi.menu().then(setMenu).catch((err) => setError(errorText(err))), []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, done?: () => void) => {
    setError(null);
    try { await fn(); done?.(); await load(); } catch (err) { setError(errorText(err)); }
  };

  if (!menu) return <p className="pos-muted">{error ?? 'Loading...'}</p>;

  return (
    <>
      {error && <div className="pos-msg err">{error}</div>}
      <div className="pos-floor-head">
        <button className="pos-btn gold" onClick={() => setCat({ name: '', station: 'Kitchen', sortOrder: menu.categories.length, isActive: true })}>+ Category</button>
        <span className="pos-muted" style={{ fontSize: 13 }}>Kitchen categories print a KOT, bar categories print a BOT. Drinks with alcohol usually take VAT (state rate), food takes 5% GST.</span>
      </div>
      {menu.categories.length === 0 && <div className="pos-panel">Start with a category such as "Starters", "Main course" or "Beer".</div>}
      {menu.categories.map((c) => {
        const items = menu.items.filter((i) => i.categoryId === c.id);
        return (
          <div key={c.id} className="pos-panel" style={{ marginBottom: 12, opacity: c.isActive ? 1 : 0.6 }}>
            <div className="pos-floor-head" style={{ marginBottom: 8 }}>
              <b style={{ fontSize: 17 }}>{c.name}</b>
              <span className="pos-muted">{c.station === 'Bar' ? 'Bar (BOT)' : 'Kitchen (KOT)'}{c.isActive ? '' : ' · hidden'}</span>
              <span style={{ flex: 1 }} />
              <button className="pos-btn sm" onClick={() => setCat(c)}>Edit</button>
              <button className="pos-btn sm gold" onClick={() => setItem(blankItem(c.id))}>+ Item</button>
              {items.length === 0 && <button className="pos-btn sm red" onClick={() => window.confirm(`Delete ${c.name}?`) && run(() => posApi.deleteCategory(c.id))}>Delete</button>}
            </div>
            {items.length > 0 && (
              <table className="pos-table-list">
                <thead><tr><th>Item</th><th>Price</th><th>Tax</th><th>Available</th><th /></tr></thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id}>
                      <td><span className={`pos-mark ${i.foodType}`} />{i.name}{i.code ? <span className="pos-muted"> · {i.code}</span> : null}</td>
                      <td>{i.variants.map((v) => (i.variants.length > 1 ? `${v.name} ${rupees(v.price)}` : rupees(v.price))).join(' · ')}</td>
                      <td>{i.taxName === 'None' ? 'None' : `${i.taxName} ${i.taxRate}%`}</td>
                      <td><button className={`pos-btn sm ${i.isAvailable ? '' : 'red'}`} onClick={() => run(() => posApi.setAvailability(i.id, !i.isAvailable))}>{i.isAvailable ? 'Yes' : 'Sold out'}</button></td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="pos-btn sm" onClick={() => setItem({ ...i, id: i.id, variants: i.variants.map((v) => ({ id: v.id, name: v.name, price: String(v.price) })) })}>Edit</button>{' '}
                        <button className="pos-btn sm red" onClick={() => window.confirm(`Delete ${i.name}?`) && run(() => posApi.deleteItem(i.id))}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {cat && (
        <div className="pos-dialog" role="dialog" aria-modal="true" aria-label="Category">
          <div>
            <h3>{cat.id ? 'Edit category' : 'New category'}</h3>
            <label className="pos-field">Name<input className="pos-input" value={cat.name ?? ''} onChange={(e) => setCat({ ...cat, name: e.target.value })} maxLength={60} autoFocus /></label>
            <div className="pos-row">
              <label className="pos-field">Prints at
                <select value={cat.station} onChange={(e) => setCat({ ...cat, station: e.target.value as MenuCategory['station'] })}>
                  <option value="Kitchen">Kitchen (KOT)</option><option value="Bar">Bar (BOT)</option>
                </select>
              </label>
              <label className="pos-field">Order<input className="pos-input" type="number" value={cat.sortOrder ?? 0} onChange={(e) => setCat({ ...cat, sortOrder: Number(e.target.value) })} /></label>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={cat.isActive ?? true} onChange={(e) => setCat({ ...cat, isActive: e.target.checked })} /> Show on the order screen</label>
            <div className="foot">
              <button className="pos-btn" onClick={() => setCat(null)}>Cancel</button>
              <button className="pos-btn gold" disabled={!cat.name?.trim()} onClick={() => run(() => posApi.saveCategory(cat.id ?? null, {
                name: cat.name!.trim(), station: cat.station ?? 'Kitchen', sortOrder: cat.sortOrder ?? 0, isActive: cat.isActive ?? true
              }), () => setCat(null))}>Save</button>
            </div>
          </div>
        </div>
      )}
      {item && <ItemDialog draft={item} categories={menu.categories} onClose={() => setItem(null)} onSave={(d) => run(() => posApi.saveItem(d.id, {
        categoryId: d.categoryId, name: d.name.trim(), code: d.code?.trim() || null, description: d.description?.trim() || null, foodType: d.foodType,
        taxName: d.taxName, taxRate: d.taxName === 'None' ? 0 : Number(d.taxRate), isAvailable: d.isAvailable, sortOrder: d.sortOrder,
        variants: d.variants.map((v) => ({ id: v.id, name: v.name.trim() || 'Regular', price: Number(v.price) }))
      }), () => setItem(null))} />}
    </>
  );
}

function ItemDialog({ draft, categories, onClose, onSave }: { draft: ItemDraft; categories: MenuCategory[]; onClose: () => void; onSave: (d: ItemDraft) => void }) {
  const [d, setD] = useState(draft);
  const set = (patch: Partial<ItemDraft>) => setD({ ...d, ...patch });
  const valid = d.name.trim() && d.variants.length > 0 && d.variants.every((v) => Number(v.price) > 0);
  return (
    <div className="pos-dialog" role="dialog" aria-modal="true" aria-label="Menu item">
      <div style={{ width: 'min(560px,100%)' }}>
        <h3>{d.id ? 'Edit item' : 'New item'}</h3>
        <div className="pos-row">
          <label className="pos-field">Name<input className="pos-input" value={d.name} onChange={(e) => set({ name: e.target.value })} maxLength={80} autoFocus /></label>
          <label className="pos-field">Short code (optional)<input className="pos-input" value={d.code ?? ''} onChange={(e) => set({ code: e.target.value })} maxLength={12} placeholder="e.g. PT1" /></label>
        </div>
        <div className="pos-row">
          <label className="pos-field">Category
            <select value={d.categoryId} onChange={(e) => set({ categoryId: Number(e.target.value) })}>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          </label>
          <label className="pos-field">Type
            <select value={d.foodType} onChange={(e) => set({ foodType: e.target.value as ItemDraft['foodType'] })}>
              <option value="Veg">Veg</option><option value="NonVeg">Non-veg</option><option value="Egg">Egg</option><option value="Drink">Drink</option>
            </select>
          </label>
        </div>
        <div className="pos-row">
          <label className="pos-field">Tax
            <select value={d.taxName} onChange={(e) => {
              const taxName = e.target.value as ItemDraft['taxName'];
              set({ taxName, taxRate: taxName === 'GST' ? 5 : taxName === 'VAT' ? 20 : 0 });
            }}>
              <option value="GST">GST (CGST + SGST)</option><option value="VAT">VAT (liquor)</option><option value="None">No tax</option>
            </select>
          </label>
          {d.taxName !== 'None' && <label className="pos-field">Rate %<input className="pos-input" inputMode="decimal" value={d.taxRate} onChange={(e) => set({ taxRate: e.target.value as unknown as number })} /></label>}
        </div>
        <label className="pos-field">Prices (add sizes such as Half/Full or 30 ml/60 ml)</label>
        {d.variants.map((v, i) => (
          <div key={i} className="pos-floor-head" style={{ marginBottom: 8 }}>
            <input className="pos-input" style={{ flex: 1 }} value={v.name} placeholder="Size" aria-label="Size" maxLength={30}
              onChange={(e) => set({ variants: d.variants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
            <input className="pos-input" style={{ width: 120 }} value={v.price} placeholder="Price ₹" aria-label="Price" inputMode="decimal"
              onChange={(e) => set({ variants: d.variants.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)) })} />
            {d.variants.length > 1 && <button className="pos-btn sm red" onClick={() => set({ variants: d.variants.filter((_, j) => j !== i) })} aria-label="Remove size">✕</button>}
          </div>
        ))}
        <button className="pos-btn sm" onClick={() => set({ variants: [...d.variants, { name: '', price: '' }] })}>+ Size</button>
        <div className="foot">
          <button className="pos-btn" onClick={onClose}>Cancel</button>
          <button className="pos-btn gold" disabled={!valid} onClick={() => onSave(d)}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ---------- tables ----------

function TablesSetup() {
  const [floor, setFloor] = useState<PosFloor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [area, setArea] = useState<Partial<PosArea> | null>(null);
  const [table, setTable] = useState<Partial<PosTable> | null>(null);
  const [bulk, setBulk] = useState<{ prefix: string; from: number; to: number; seats: number; areaId: number | null } | null>(null);
  const load = useCallback(() => posApi.floor().then(setFloor).catch((err) => setError(errorText(err))), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn: () => Promise<unknown>, done?: () => void) => {
    setError(null);
    try { await fn(); done?.(); await load(); } catch (err) { setError(errorText(err)); await load(); }
  };
  if (!floor) return <p className="pos-muted">{error ?? 'Loading...'}</p>;

  const addBulk = async (b: NonNullable<typeof bulk>) => {
    for (let n = b.from; n <= b.to; n++) {
      // eslint-disable-next-line no-await-in-loop
      await posApi.saveTable(null, { areaId: b.areaId, name: `${b.prefix}${n}`, seats: b.seats, sortOrder: n, isActive: true });
    }
  };
  const groups: { id: number | null; name: string }[] = [...floor.areas.map((a) => ({ id: a.id as number | null, name: a.name })), { id: null, name: 'No area' }];

  return (
    <>
      {error && <div className="pos-msg err">{error}</div>}
      <div className="pos-floor-head">
        <button className="pos-btn" onClick={() => setArea({ name: '', sortOrder: floor.areas.length })}>+ Area</button>
        <button className="pos-btn" onClick={() => setTable({ name: '', seats: 4, sortOrder: floor.tables.length, isActive: true, areaId: floor.areas[0]?.id ?? null })}>+ Table</button>
        <button className="pos-btn gold" onClick={() => setBulk({ prefix: 'T', from: 1, to: 10, seats: 4, areaId: floor.areas[0]?.id ?? null })}>Add many tables</button>
        <span className="pos-muted" style={{ fontSize: 13 }}>Areas group tables, e.g. Indoor, Terrace, Bar counter.</span>
      </div>
      {groups.map((g) => {
        const tables = floor.tables.filter((t) => (t.areaId ?? null) === g.id);
        if (g.id === null && tables.length === 0) return null;
        const a = floor.areas.find((x) => x.id === g.id);
        return (
          <div key={g.id ?? 'none'} className="pos-panel" style={{ marginBottom: 12 }}>
            <div className="pos-floor-head" style={{ marginBottom: 8 }}>
              <b style={{ fontSize: 17 }}>{g.name}</b><span style={{ flex: 1 }} />
              {a && <button className="pos-btn sm" onClick={() => setArea(a)}>Rename</button>}
              {a && tables.length === 0 && <button className="pos-btn sm red" onClick={() => run(() => posApi.deleteArea(a.id))}>Delete</button>}
            </div>
            <div className="pos-tables">
              {tables.length === 0 && <span className="pos-muted">No tables.</span>}
              {tables.map((t) => (
                <button key={t.id} className="pos-table" style={{ minHeight: 80, opacity: t.isActive ? 1 : 0.5 }} onClick={() => setTable(t)}>
                  <b>{t.name}</b><small>{t.seats} seats{t.isActive ? '' : ' · hidden'}{t.openOrder ? ' · in use' : ''}</small>
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {area && (
        <div className="pos-dialog" role="dialog" aria-modal="true" aria-label="Area">
          <div>
            <h3>{area.id ? 'Rename area' : 'New area'}</h3>
            <label className="pos-field">Name<input className="pos-input" value={area.name ?? ''} onChange={(e) => setArea({ ...area, name: e.target.value })} maxLength={40} autoFocus /></label>
            <div className="foot">
              <button className="pos-btn" onClick={() => setArea(null)}>Cancel</button>
              <button className="pos-btn gold" disabled={!area.name?.trim()} onClick={() => run(() => posApi.saveArea(area.id ?? null, { name: area.name!.trim(), sortOrder: area.sortOrder ?? 0 }), () => setArea(null))}>Save</button>
            </div>
          </div>
        </div>
      )}
      {table && (
        <div className="pos-dialog" role="dialog" aria-modal="true" aria-label="Table">
          <div>
            <h3>{table.id ? `Table ${table.name}` : 'New table'}</h3>
            <div className="pos-row">
              <label className="pos-field">Name / number<input className="pos-input" value={table.name ?? ''} onChange={(e) => setTable({ ...table, name: e.target.value })} maxLength={20} autoFocus /></label>
              <label className="pos-field">Seats<input className="pos-input" type="number" min={1} value={table.seats ?? 4} onChange={(e) => setTable({ ...table, seats: Number(e.target.value) })} /></label>
            </div>
            <label className="pos-field">Area
              <select value={table.areaId ?? ''} onChange={(e) => setTable({ ...table, areaId: e.target.value ? Number(e.target.value) : null })}>
                <option value="">No area</option>{floor.areas.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={table.isActive ?? true} onChange={(e) => setTable({ ...table, isActive: e.target.checked })} /> Show on the floor</label>
            <div className="foot">
              {table.id && !table.openOrder && <button className="pos-btn red" onClick={() => run(() => posApi.deleteTable(table.id!), () => setTable(null))}>Delete</button>}
              <button className="pos-btn" onClick={() => setTable(null)}>Cancel</button>
              <button className="pos-btn gold" disabled={!table.name?.trim() || (table.seats ?? 0) < 1} onClick={() => run(() => posApi.saveTable(table.id ?? null, {
                areaId: table.areaId ?? null, name: table.name!.trim(), seats: table.seats ?? 4, sortOrder: table.sortOrder ?? 0, isActive: table.isActive ?? true
              }), () => setTable(null))}>Save</button>
            </div>
          </div>
        </div>
      )}
      {bulk && (
        <div className="pos-dialog" role="dialog" aria-modal="true" aria-label="Add many tables">
          <div>
            <h3>Add many tables</h3>
            <div className="pos-row">
              <label className="pos-field">Prefix<input className="pos-input" value={bulk.prefix} onChange={(e) => setBulk({ ...bulk, prefix: e.target.value })} maxLength={8} /></label>
              <label className="pos-field">From<input className="pos-input" type="number" min={1} value={bulk.from} onChange={(e) => setBulk({ ...bulk, from: Number(e.target.value) })} /></label>
              <label className="pos-field">To<input className="pos-input" type="number" min={1} value={bulk.to} onChange={(e) => setBulk({ ...bulk, to: Number(e.target.value) })} /></label>
              <label className="pos-field">Seats<input className="pos-input" type="number" min={1} value={bulk.seats} onChange={(e) => setBulk({ ...bulk, seats: Number(e.target.value) })} /></label>
            </div>
            <label className="pos-field">Area
              <select value={bulk.areaId ?? ''} onChange={(e) => setBulk({ ...bulk, areaId: e.target.value ? Number(e.target.value) : null })}>
                <option value="">No area</option>{floor.areas.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </label>
            <p className="pos-muted">Creates {bulk.prefix}{bulk.from} to {bulk.prefix}{bulk.to}.</p>
            <div className="foot">
              <button className="pos-btn" onClick={() => setBulk(null)}>Cancel</button>
              <button className="pos-btn gold" disabled={bulk.to < bulk.from || bulk.to - bulk.from > 99 || bulk.seats < 1} onClick={() => run(() => addBulk(bulk), () => setBulk(null))}>Add {Math.max(0, bulk.to - bulk.from + 1)} tables</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------- staff ----------

const ROLE_HELP: Record<Exclude<PosRole, 'Owner'>, string> = {
  Manager: 'Everything except staff: discounts, voids, cancel, reports, day close, setup.',
  Cashier: 'Orders, bills and payments.',
  Captain: 'Takes orders and sends KOT/BOT.'
};

function StaffSetup() {
  const { me } = usePos();
  const [staff, setStaff] = useState<PosStaff[] | null>(null);
  const [edit, setEdit] = useState<{ id: number | null; name: string; role: string; pin: string; isActive: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => posApi.staff().then(setStaff).catch((err) => setError(errorText(err))), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!edit) return;
    setError(null);
    try {
      await posApi.saveStaff(edit.id, { name: edit.name.trim(), role: edit.role, pin: edit.pin || undefined, isActive: edit.isActive });
      setEdit(null);
      load();
    } catch (err) { setError(errorText(err)); }
  };
  return (
    <>
      <div className="pos-panel" style={{ marginBottom: 12 }}>
        Staff sign in on any tablet at <b>{window.location.host}/pos/login</b> with venue code <b style={{ color: 'var(--pos-gold)', letterSpacing: 2 }}>{me.settings.venueCode}</b> and their own PIN.
        Every bill, void and discount is recorded with the staff member's name.
      </div>
      {error && !edit && <div className="pos-msg err">{error}</div>}
      <button className="pos-btn gold" style={{ marginBottom: 12 }} onClick={() => setEdit({ id: null, name: '', role: 'Captain', pin: '', isActive: true })}>+ Staff member</button>
      {staff === null ? <p className="pos-muted">Loading...</p> : (
        <div className="pos-panel">
          <table className="pos-table-list">
            <thead><tr><th>Name</th><th>Role</th><th>Status</th><th>Last sign-in</th><th /></tr></thead>
            <tbody>
              {staff.length === 0 && <tr><td colSpan={5} className="pos-muted">No staff yet.</td></tr>}
              {staff.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td><td>{s.role}</td><td>{s.isActive ? 'Active' : 'Disabled'}</td>
                  <td>{s.lastLoginAt ? new Date(s.lastLoginAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                  <td style={{ textAlign: 'right' }}><button className="pos-btn sm" onClick={() => setEdit({ id: s.id, name: s.name, role: s.role, pin: '', isActive: s.isActive })}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && (
        <div className="pos-dialog" role="dialog" aria-modal="true" aria-label="Staff member">
          <div>
            <h3>{edit.id ? `Edit ${edit.name}` : 'New staff member'}</h3>
            {error && <div className="pos-msg err">{error}</div>}
            <label className="pos-field">Name<input className="pos-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} maxLength={60} autoFocus /></label>
            <label className="pos-field">Role
              <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
                {(Object.keys(ROLE_HELP) as (keyof typeof ROLE_HELP)[]).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <span>{ROLE_HELP[edit.role as keyof typeof ROLE_HELP]}</span>
            </label>
            <label className="pos-field">{edit.id ? 'New PIN (leave empty to keep)' : 'PIN (4 to 6 digits)'}
              <input className="pos-input" inputMode="numeric" value={edit.pin} onChange={(e) => setEdit({ ...edit, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })} autoComplete="off" />
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={edit.isActive} onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} /> Can sign in</label>
            <div className="foot">
              <button className="pos-btn" onClick={() => { setEdit(null); setError(null); }}>Cancel</button>
              <button className="pos-btn gold" disabled={!edit.name.trim() || (!edit.id && edit.pin.length < 4) || (edit.pin.length > 0 && edit.pin.length < 4)} onClick={save}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------- bill ----------

function BillSetup() {
  const { me, reloadMe } = usePos();
  const s = me.settings;
  const [form, setForm] = useState({
    legalName: s.legalName ?? '', gstin: s.gstin ?? '', fssaiNumber: s.fssaiNumber ?? '', billAddress: s.billAddress ?? '', billPhone: s.billPhone ?? '',
    invoicePrefix: s.invoicePrefix, serviceChargePercent: String(s.serviceChargePercent), billFooter: s.billFooter ?? ''
  });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setMsg(null);
    try {
      await posApi.saveSettings({
        legalName: form.legalName.trim() || null, gstin: form.gstin.trim().toUpperCase() || null, fssaiNumber: form.fssaiNumber.trim() || null,
        billAddress: form.billAddress.trim() || null, billPhone: form.billPhone.trim() || null, invoicePrefix: form.invoicePrefix.trim() || 'INV',
        serviceChargePercent: Number(form.serviceChargePercent) || 0, billFooter: form.billFooter.trim() || null
      });
      reloadMe();
      setMsg({ ok: true, text: 'Saved. New bills use these details.' });
    } catch (err) { setMsg({ ok: false, text: errorText(err) }); }
  };
  return (
    <div className="pos-panel" style={{ maxWidth: 720 }}>
      {msg && <div className={`pos-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</div>}
      <div className="pos-row">
        <label className="pos-field">Legal / registered name<input className="pos-input" value={form.legalName} onChange={set('legalName')} maxLength={120} placeholder={me.venueName} /></label>
        <label className="pos-field">Phone on bill<input className="pos-input" value={form.billPhone} onChange={set('billPhone')} maxLength={20} /></label>
      </div>
      <label className="pos-field">Address on bill<textarea rows={2} value={form.billAddress} onChange={set('billAddress')} maxLength={300} /></label>
      <div className="pos-row">
        <label className="pos-field">GSTIN (makes the bill a tax invoice)<input className="pos-input" value={form.gstin} onChange={set('gstin')} maxLength={15} placeholder="27ABCDE1234F1Z5" /></label>
        <label className="pos-field">FSSAI licence no.<input className="pos-input" inputMode="numeric" value={form.fssaiNumber} onChange={set('fssaiNumber')} maxLength={14} /></label>
      </div>
      <div className="pos-row">
        <label className="pos-field">Bill number prefix<input className="pos-input" value={form.invoicePrefix} onChange={set('invoicePrefix')} maxLength={10} /></label>
        <label className="pos-field">Service charge % (optional for guests)<input className="pos-input" inputMode="decimal" value={form.serviceChargePercent} onChange={set('serviceChargePercent')} /></label>
      </div>
      <p className="pos-muted" style={{ fontSize: 13, marginTop: -4 }}>Bills are numbered {form.invoicePrefix || 'INV'}/YY-YY/000001 and restart every financial year (1 April), as GST requires.</p>
      <label className="pos-field">Footer<textarea rows={2} value={form.billFooter} onChange={set('billFooter')} maxLength={300} placeholder="Thank you! Visit again." /></label>
      <button className="pos-btn gold" onClick={save}>Save bill settings</button>
    </div>
  );
}

export default SetupPage;
